// Chart › Timeline › lab trend cards and "Compare results" (below care-model's Timeline lanes).
// Pure logic only: which tests get a card, where the latest value sits against the lab range, the change
// since the first result in the range, and the Compare chart's ECharts option. Every value comes from the
// patient's own results (no made-up data); a flag is the lab's own (labFlag), never computed.
import type { EChartsOption } from 'echarts';
import type { LabObservationTrendPoint } from '@/types/lab-results-api';
import { CLINIC_TIME_ZONE } from '@/shared/lib/clinic-time';
import {
  labInstant,
  labValueComparator,
  isWithdrawn,
  type LabAnalyteRow,
  type LabResultGroup,
  type LabTrendRow,
} from '@/patients/lib/lab-results';

/** The Timeline's range: ISO dates (YYYY-MM-DD), both inclusive; `from` null = all time. */
export interface TimelineRange {
  from: string | null;
  to: string;
}

/** One flagged test's card: the latest flagged result in the range, plus where it came from. */
export interface LabTrendCardModel {
  /** Stable across renders: the trend key + unit + lab + range. */
  key: string;
  row: LabAnalyteRow;
  /** The clinic date (YYYY-MM-DD) of that result. */
  date: string;
}

/** A plotted point: when (ms) and the value. */
export interface LabTrendPoint {
  at: number;
  value: number;
}

const clinicIsoDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: CLINIC_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** An instant (ms) as the clinic's calendar date, YYYY-MM-DD; undefined when not a real instant. */
export function clinicDayOf(at: number): string | undefined {
  return Number.isFinite(at) ? clinicIsoDate.format(new Date(at)) : undefined;
}

export function inRange(day: string | undefined, range: TimelineRange): boolean {
  if (!day) return false;
  if (range.from && day < range.from) return false;
  return day <= range.to;
}

/** H, L and the critical flags (HH, LL, AA): Andrew's ruling (a). A plain "A" (abnormal) gets no card. */
export function isCardFlag(row: Pick<LabAnalyteRow, 'flag'>): boolean {
  return !!row.flag && (row.flag.critical || row.flag.code === 'H' || row.flag.code === 'L');
}

function seriesKey(row: LabAnalyteRow): string | null {
  const k = row.trendKey;
  if (!k) return null;
  const code = k.loinc
    ? `loinc:${k.loinc}`
    : k.scripsComponentCode
      ? `scrips:${k.scripsComponentSystem ?? ''}|${k.scripsComponentCode}`
      : `local:${k.localSystem ?? ''}|${k.localCode ?? ''}`;
  const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase();
  return [code, norm(row.unit), norm(row.lab), row.refLow ?? '', row.refHigh ?? '', norm(row.refText), norm(row.rangeRegime)].join('§');
}

/**
 * Every test with an H/L/critical result inside the range gets a card, one per series (code + unit + lab +
 * range, the same key the trend line joins on). The card shows that series' LATEST result in the range,
 * flagged or not, as the demo's cards do ("2.48 mmol/L · Within lab range"): a newer result inside the lab
 * range must win over an older flagged one (Saeed, 4 Nov: 26 Oct 2026 was skipped for 8 Dec 2025).
 * Withdrawn or superseded rows never get here (the Labs tab builds only current rows). Newest first.
 */
export function flaggedTestCards(groups: LabResultGroup[], range: TimelineRange): LabTrendCardModel[] {
  const latest = new Map<string, LabTrendCardModel & { at: number }>();
  const flagged = new Set<string>();
  for (const group of groups) {
    const rows = [...group.tests.flatMap((t) => t.analytes), ...group.otherAnalytes];
    for (const row of rows) {
      const key = seriesKey(row);
      if (!key) continue;
      // The collection time when recorded, else the request/report date of its group.
      const at = labInstant(row.collectedAt ?? group.date);
      const date = clinicDayOf(at);
      if (!date || !inRange(date, range)) continue;
      if (isCardFlag(row)) flagged.add(key);
      const seen = latest.get(key);
      // Same instant: the higher row id wins (numerically when the ids are numbers: "10" after "9"), so the pick never depends
      // on the order the groups arrive in.
      if (!seen || at > seen.at || (at === seen.at && byId.compare(row.id, seen.row.id) > 0)) latest.set(key, { key, row, date, at });
    }
  }
  return [...latest.values()]
    .filter((c) => flagged.has(c.key))
    .sort((a, b) => b.at - a.at)
    .map(({ key, row, date }) => ({ key, row, date }));
}

const norm = (t: string | null | undefined) => (t ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
const byId = new Intl.Collator('en', { numeric: true });
const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * The plotted points for one card, with their times: the same join rule as `trendSeries` (unit, performing
 * lab and range must all match; numeric, no comparator, current, not withdrawn), oldest first, limited to the
 * range.
 */
export function trendPointsInRange(
  points: LabObservationTrendPoint[] | null | undefined,
  row: LabTrendRow,
  labs: ReadonlyMap<string, string>,
  range: TimelineRange,
): LabTrendPoint[] {
  const wantUnit = norm(row.unit);
  const wantLab = norm(row.lab);
  return (points ?? [])
    .filter((p) => isNum(p.valueNumeric))
    .filter((p) => labValueComparator(p) === null)
    .filter((p) => p.isCurrent !== false)
    .filter((p) => !isWithdrawn(p.status) && !isWithdrawn(p.reportStatus))
    .filter((p) => {
      if (norm(p.unit ?? p.unitText) !== wantUnit) return false;
      const lab = norm(labs.get(p.rootLabReportId ?? ''));
      if (!wantLab || !lab || lab !== wantLab) return false;
      return (
        (isNum(p.refLow) ? p.refLow : null) === row.refLow &&
        (isNum(p.refHigh) ? p.refHigh : null) === row.refHigh &&
        norm(p.refText) === norm(row.refText) &&
        norm(p.rangeRegime) === norm(row.rangeRegime)
      );
    })
    .map((p, i) => ({ at: labInstant(p.effectiveAt ?? p.issuedAt), value: p.valueNumeric as number, i }))
    .filter((p) => inRange(clinicDayOf(p.at), range))
    .sort((a, b) => a.at - b.at || a.i - b.i)
    .map(({ at, value }) => ({ at, value }));
}

export type RangePosition = 'above' | 'below' | 'within';

/** Where a value sits against the lab's numeric range; null when the lab gave no numeric bound. */
export function rangePosition(value: number, low: number | null, high: number | null): RangePosition | null {
  if (low === null && high === null) return null;
  if (high !== null && value > high) return 'above';
  if (low !== null && value < low) return 'below';
  return 'within';
}

export function rangePositionText(position: RangePosition | null): string | null {
  if (position === 'above') return 'Above range';
  if (position === 'below') return 'Below range';
  if (position === 'within') return 'Within range';
  return null;
}

/** The change from the first plotted point in the range to the last; null with fewer than 2 points. */
export function changeSinceFirst(points: LabTrendPoint[]): { delta: number; sinceDay: string } | null {
  if (points.length < 2) return null;
  const first = points[0];
  const last = points[points.length - 1];
  const sinceDay = clinicDayOf(first.at);
  if (!sinceDay) return null;
  return { delta: last.value - first.value, sinceDay };
}

/** "+0.8 since 12 Mar 2025" / "No change since …"; the delta keeps the result's own precision. */
export function changeText(change: { delta: number; sinceDay: string } | null, unit: string): string | null {
  if (!change) return null;
  const since = formatDay(change.sinceDay);
  const rounded = Math.round(change.delta * 100) / 100;
  if (rounded === 0) return `No change since ${since}`;
  const sign = rounded > 0 ? '+' : '−';
  return `${sign}${Math.abs(rounded)}${unit ? ` ${unit}` : ''} since ${since}`;
}

const DAY_TEXT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** YYYY-MM-DD as "12 Mar 2025" (the day itself, no zone shift). */
export function formatDay(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return m ? DAY_TEXT.format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))) : day;
}

// ─── Compare results ────────────────────────────────────────────────────────

export type CompareMode = 'overlay' | 'side-by-side';

export interface CompareSeries {
  name: string;
  unit: string;
  points: LabTrendPoint[];
  refLow: number | null;
  refHigh: number | null;
}

/** Colours are read from Signal DS tokens by the caller (CSS variables), never hard-coded here. */
export interface CompareColors {
  series: [string | undefined, string | undefined];
  axis?: string;
  grid?: string;
  text?: string;
  band?: string;
}

function seriesOption(s: CompareSeries, color: string | undefined, yAxisIndex: number, colors: CompareColors, withBand: boolean) {
  const hasBand = withBand && s.refLow !== null && s.refHigh !== null;
  return {
    type: 'line' as const,
    name: s.unit ? `${s.name} (${s.unit})` : s.name,
    yAxisIndex,
    data: s.points.map((p) => [p.at, p.value]),
    showSymbol: true,
    symbolSize: 6,
    lineStyle: { color, width: 2 },
    itemStyle: { color },
    ...(hasBand
      ? {
          markArea: {
            silent: true,
            itemStyle: { color: colors.band },
            data: [[{ yAxis: s.refLow as number }, { yAxis: s.refHigh as number }]],
          },
        }
      : {}),
  };
}

function axisStyle(colors: CompareColors) {
  return {
    axisLine: { lineStyle: { color: colors.axis } },
    axisLabel: { color: colors.text },
    splitLine: { lineStyle: { color: colors.grid } },
  };
}

/**
 * The Compare chart for one panel. Overlay: both tests on one time axis, each on its own y axis (left and
 * right) so different units are never forced onto one scale; the lab range band only when one test is
 * shown (two bands on two scales would mislead). Side by side: call once per test.
 */
export function compareOption(series: CompareSeries[], colors: CompareColors): EChartsOption {
  const two = series.length === 2;
  return {
    animation: false,
    grid: { left: 48, right: two ? 48 : 16, top: 32, bottom: 28 },
    legend: { top: 0, textStyle: { color: colors.text } },
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'time', ...axisStyle(colors), splitLine: { show: false } },
    yAxis: series.map((s, i) => ({
      type: 'value' as const,
      scale: true,
      name: s.unit || undefined,
      nameTextStyle: { color: colors.text },
      position: i === 0 ? ('left' as const) : ('right' as const),
      ...axisStyle(colors),
      ...(i > 0 ? { splitLine: { show: false } } : {}),
    })),
    series: series.map((s, i) => seriesOption(s, colors.series[i % 2], i, colors, !two)),
  } as EChartsOption;
}
